import { useEffect, useState } from "react";
import Shell from "../../components/Shell";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Bell } from "lucide-react";

const EMPTY = { title: "", description: "", location: "", starts_at: "", ends_at: "", capacity: 0, is_active: true };

function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminEvents() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [registrations, setRegistrations] = useState(null);

    const reload = async () => { const { data } = await api.get("/admin/events"); setList(data || []); };
    useEffect(() => { reload(); }, []);

    const save = async () => {
        try {
            const payload = {
                ...editing,
                capacity: parseInt(editing.capacity || 0, 10),
                starts_at: editing.starts_at ? new Date(editing.starts_at).toISOString() : "",
                ends_at: editing.ends_at ? new Date(editing.ends_at).toISOString() : null,
            };
            if (editing.id) await api.put(`/admin/events/${editing.id}`, payload);
            else await api.post("/admin/events", payload);
            toast.success("Saved");
            setEditing(null); reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this event and all its registrations?")) return;
        await api.delete(`/admin/events/${id}`); toast.success("Deleted"); reload();
    };
    const viewRegs = async (ev) => {
        const { data } = await api.get(`/admin/events/${ev.id}/registrations`);
        setRegistrations({ event: ev, list: data || [] });
    };
    const sendReminders = async (ev) => {
        if (!window.confirm(`Send reminder email to all registrants for "${ev.title}"?`)) return;
        try {
            const { data } = await api.post(`/admin/events/${ev.id}/send-reminders`);
            toast.success(`Reminder sent to ${data.sent} registrant${data.sent === 1 ? "" : "s"}`);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <Shell variant="admin">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <div className="flex items-baseline justify-between mb-8">
                    <div><p className="eyebrow mb-2">Training & events</p><h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight">Events</h1></div>
                    <button data-testid="new-event-btn" onClick={() => setEditing({ ...EMPTY })} className="btn-primary"><Plus size={14} strokeWidth={1.5} /> New event</button>
                </div>

                <div className="card-base overflow-hidden" data-testid="events-table">
                    <table className="w-full text-left">
                        <thead className="bg-zinc-50/60 border-b border-zinc-200">
                            <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                                <th className="py-3 px-4">Title</th><th className="py-3 px-4">When</th><th className="py-3 px-4">Location</th><th className="py-3 px-4">Capacity</th><th className="py-3 px-4">Registered</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(ev => (
                                <tr key={ev.id} className="border-b border-zinc-100">
                                    <td className="py-3 px-4 text-sm font-medium">{ev.title}<br /><span className="text-[10px] uppercase tracking-widest text-zinc-400">{ev.is_active ? "Published" : "Hidden"}</span></td>
                                    <td className="py-3 px-4 text-sm">{new Date(ev.starts_at).toLocaleString()}</td>
                                    <td className="py-3 px-4 text-sm">{ev.location || "—"}</td>
                                    <td className="py-3 px-4 text-sm">{ev.capacity > 0 ? ev.capacity : "∞"}</td>
                                    <td className="py-3 px-4 text-sm">
                                        <button data-testid={`event-regs-${ev.id}`} onClick={() => viewRegs(ev)} className="btn-ghost inline-flex items-center gap-1"><Users size={13} strokeWidth={1.5} /> {ev.registrations_count}</button>
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        <button data-testid={`event-remind-${ev.id}`} onClick={() => sendReminders(ev)} className="btn-ghost" title="Send reminder now"><Bell size={14} strokeWidth={1.5} /></button>
                                        <button data-testid={`event-edit-${ev.id}`} onClick={() => setEditing({ ...ev, starts_at: toLocalInput(ev.starts_at), ends_at: toLocalInput(ev.ends_at) })} className="btn-ghost"><Pencil size={14} strokeWidth={1.5} /></button>
                                        <button data-testid={`event-delete-${ev.id}`} onClick={() => del(ev.id)} className="btn-ghost text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                                    </td>
                                </tr>
                            ))}
                            {list.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sm text-zinc-500">No events yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setEditing(null)}>
                    <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <p className="eyebrow mb-1">{editing.id ? "Edit event" : "New event"}</p>
                        <h2 className="font-heading text-2xl mb-6">{editing.title || "Untitled"}</h2>
                        <div className="space-y-4">
                            <div><label className="input-label">Title</label><input data-testid="event-title" className="input-base" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
                            <div><label className="input-label">Description</label><textarea data-testid="event-description" rows={4} className="input-base" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
                            <div><label className="input-label">Location</label><input data-testid="event-location" className="input-base" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="e.g. Dermalis Auckland HQ" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="input-label">Starts</label><input data-testid="event-starts" type="datetime-local" className="input-base" value={editing.starts_at} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} /></div>
                                <div><label className="input-label">Ends (optional)</label><input data-testid="event-ends" type="datetime-local" className="input-base" value={editing.ends_at || ""} onChange={(e) => setEditing({ ...editing, ends_at: e.target.value })} /></div>
                            </div>
                            <div><label className="input-label">Capacity (0 = unlimited)</label><input data-testid="event-capacity" type="number" min={0} className="input-base" value={editing.capacity} onChange={(e) => setEditing({ ...editing, capacity: e.target.value })} /></div>
                            <label className="flex items-center gap-2 text-sm"><input data-testid="event-active" type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Published</label>
                        </div>
                        <div className="flex gap-3 mt-8 pt-6 border-t border-zinc-200">
                            <button data-testid="event-save" onClick={save} className="btn-primary">Save</button>
                            <button onClick={() => setEditing(null)} className="btn-ghost ml-auto">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {registrations && (
                <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setRegistrations(null)}>
                    <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <p className="eyebrow mb-1">Registrations</p>
                        <h2 className="font-heading text-2xl mb-6">{registrations.event.title}</h2>
                        <div className="card-base divide-y divide-zinc-100">
                            {registrations.list.map(r => (
                                <div key={r.id} className="p-4">
                                    <p className="font-medium text-sm">{r.clinic_name}</p>
                                    <p className="text-xs text-zinc-500">{r.contact_name} · {r.email}</p>
                                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                                </div>
                            ))}
                            {registrations.list.length === 0 && <p className="p-4 text-sm text-zinc-500">No registrations yet.</p>}
                        </div>
                    </div>
                </div>
            )}
        </Shell>
    );
}
